import { useEffect, useMemo, useRef, useState } from "react";
import * as Y from "yjs";
import { MeshShellConnectionBridge, type YRoom } from "@baditaflorin/mesh-common";
import { createRoomSync } from "../sync/yjsRoom";
import { createClockSync } from "../sync/clockSync";
import { maybeFetchTurnCredentials } from "../sync/iceConfig";
import {
  startScanner,
  drawPreview,
  type MarkerEvent,
  type ScannerHandle,
} from "../markers/scanner";
import { seededShuffle } from "./shuffle";

export type Mode = "tap" | "apriltag";
export type Phase = "write" | "release" | "vote" | "results";

type Idea = { id: string; text: string };

type Session = {
  phase: Phase;
  prompt: string;
  startedAt: number;
  durationMs: number;
  releaseSeed: number; // set when entering release phase
};

type PendingPeer = {
  tagSlots: Record<string, string>; // tagId(stringified) -> idea text
};

type Props = {
  roomId: string;
  myPeerId: string;
  mode: Mode;
  tagBindings: Record<number, 1 | 2 | 3>; // tagId -> slot
  writeDurationMin: number;
  prompt: string;
  isWall: boolean;
  onOpenSettings: () => void;
};

export function Brain({
  roomId,
  myPeerId,
  mode,
  tagBindings,
  writeDurationMin,
  prompt,
  isWall,
  onOpenSettings,
}: Props) {
  const [armed, setArmed] = useState(false);
  const [session, setSession] = useState<Session>({
    phase: "write",
    prompt,
    startedAt: Date.now(),
    durationMs: writeDurationMin * 60_000,
    releaseSeed: 0,
  });
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [votes, setVotes] = useState<Record<string, Set<string>>>({});
  const [now, setNow] = useState(Date.now());
  const [peers, setPeers] = useState(0);
  const [lastMarker, setLastMarker] = useState<number | null>(null);

  // Pending ideas (per peer, 3 slots).
  const [pending1, setPending1] = useState("");
  const [pending2, setPending2] = useState("");
  const [pending3, setPending3] = useState("");

  const previewRef = useRef<HTMLCanvasElement | null>(null);
  const scannerRef = useRef<ScannerHandle | null>(null);

  const meshHandle = useMemo(() => {
    if (!armed) return null;
    const room = createRoomSync(roomId);
    const clock = createClockSync(room.provider);
    return { room, clock };
  }, [armed, roomId]);

  // This feature owns its WebRTC room rather than asking MeshShell to create
  // a second transport. Report exactly that live room once the writer has
  // chosen to enter it, so the shared chrome can show honest connection
  // diagnostics without inventing a connected state on the landing screen.
  const reportedRoom = useMemo<YRoom | null>(() => {
    if (!meshHandle) return null;
    return {
      doc: meshHandle.room.doc,
      provider: meshHandle.room.provider,
      peerId: meshHandle.room.peerId,
      deviceId: myPeerId,
      peerCount: peers,
      roomId,
    };
  }, [meshHandle, myPeerId, peers, roomId]);

  useEffect(() => {
    if (!armed) return;
    void maybeFetchTurnCredentials();
  }, [armed]);

  useEffect(() => {
    return () => {
      meshHandle?.clock.destroy();
      meshHandle?.room.provider?.destroy();
      scannerRef.current?.stop();
      scannerRef.current = null;
    };
  }, [meshHandle]);

  // Load my pending from localStorage.
  useEffect(() => {
    setPending1(localStorage.getItem("mesh-brain-write:pending1") ?? "");
    setPending2(localStorage.getItem("mesh-brain-write:pending2") ?? "");
    setPending3(localStorage.getItem("mesh-brain-write:pending3") ?? "");
  }, []);

  useEffect(() => {
    localStorage.setItem("mesh-brain-write:pending1", pending1);
    localStorage.setItem("mesh-brain-write:pending2", pending2);
    localStorage.setItem("mesh-brain-write:pending3", pending3);
  }, [pending1, pending2, pending3]);

  // Bind Yjs.
  useEffect(() => {
    if (!meshHandle) return;
    const doc = meshHandle.room.doc;
    const ySession = doc.getMap("session");
    const yIdeas = doc.getArray<Idea>("ideas");
    const yVotes = doc.getMap<Y.Map<boolean>>("votes");
    const yPending = doc.getMap<PendingPeer>("pending");

    const readSession = () => {
      setSession({
        phase: (ySession.get("phase") as Phase | undefined) ?? "write",
        prompt: (ySession.get("prompt") as string | undefined) ?? prompt,
        startedAt: (ySession.get("startedAt") as number | undefined) ?? Date.now(),
        durationMs: (ySession.get("durationMs") as number | undefined) ?? writeDurationMin * 60_000,
        releaseSeed: (ySession.get("releaseSeed") as number | undefined) ?? 0,
      });
    };
    const readIdeas = () => setIdeas(yIdeas.toArray());
    const readVotes = () => {
      const next: Record<string, Set<string>> = {};
      yVotes.forEach((m, id) => {
        const s = new Set<string>();
        m.forEach((_, peerId) => s.add(peerId));
        next[id] = s;
      });
      setVotes(next);
    };

    if (!ySession.has("phase")) {
      doc.transact(() => {
        ySession.set("phase", "write");
        ySession.set("prompt", prompt);
        ySession.set("startedAt", Date.now());
        ySession.set("durationMs", writeDurationMin * 60_000);
        ySession.set("releaseSeed", 0);
      });
    }

    readSession();
    readIdeas();
    readVotes();

    ySession.observe(readSession);
    yIdeas.observe(readIdeas);
    yVotes.observeDeep(readVotes);

    // Awareness for peer count.
    const awareness = (
      meshHandle.room.provider as unknown as {
        awareness?: {
          on: (e: string, cb: () => void) => void;
          off: (e: string, cb: () => void) => void;
          getStates: () => Map<number, unknown>;
        };
      } | null
    )?.awareness;
    const updatePeers = () => {
      if (!awareness) return;
      setPeers(Math.max(0, awareness.getStates().size - 1));
    };
    awareness?.on("change", updatePeers);
    updatePeers();

    // Always publish our pending slots — kept fresh by the effect below.
    void yPending;

    return () => {
      ySession.unobserve(readSession);
      yIdeas.unobserve(readIdeas);
      yVotes.unobserveDeep(readVotes);
      awareness?.off("change", updatePeers);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meshHandle]);

  // Push pending ideas to Yjs.
  useEffect(() => {
    if (!meshHandle) return;
    const yPending = meshHandle.room.doc.getMap<PendingPeer>("pending");
    const slots: Record<string, string> = {};
    if (pending1.trim()) slots["1"] = pending1.trim();
    if (pending2.trim()) slots["2"] = pending2.trim();
    if (pending3.trim()) slots["3"] = pending3.trim();
    // Also expose them keyed by tagId for ArUco lookup.
    const tagSlots: Record<string, string> = { ...slots };
    Object.entries(tagBindings).forEach(([tagIdStr, slotNum]) => {
      const slotKey = String(slotNum);
      const text = slots[slotKey];
      if (text) tagSlots[`tag:${tagIdStr}`] = text;
    });
    yPending.set(myPeerId, { tagSlots });
  }, [pending1, pending2, pending3, meshHandle, myPeerId, tagBindings]);

  // Animation tick.
  useEffect(() => {
    if (!meshHandle) return;
    let frame = 0;
    const tick = () => {
      setNow(meshHandle.clock.meshNow());
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [meshHandle]);

  // Auto-advance write -> release when time runs out (any phone races).
  useEffect(() => {
    if (!meshHandle) return;
    if (session.phase !== "write") return;
    const elapsed = now - session.startedAt;
    if (elapsed >= session.durationMs) {
      const ySession = meshHandle.room.doc.getMap("session");
      meshHandle.room.doc.transact(() => {
        ySession.set("phase", "release");
        ySession.set("releaseSeed", Math.floor(Date.now()));
      });
    }
  }, [meshHandle, now, session]);

  // Scanner (wall + apriltag + release).
  useEffect(() => {
    if (!armed || !isWall || mode !== "apriltag" || session.phase !== "release") {
      scannerRef.current?.stop();
      scannerRef.current = null;
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const h = await startScanner({
          width: 480,
          height: 360,
          onMarker: (m) => onScannedMarker(m, meshHandle?.room.doc ?? null),
          onFrame: (markers) => {
            if (previewRef.current && scannerRef.current) {
              drawPreview(previewRef.current, scannerRef.current.canvas, markers);
            }
          },
        });
        if (cancelled) {
          h.stop();
          return;
        }
        scannerRef.current = h;
      } catch (err) {
        console.warn("[scanner] failed:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armed, isWall, mode, session.phase, meshHandle]);

  function onScannedMarker(m: MarkerEvent, doc: Y.Doc | null) {
    if (!doc) return;
    setLastMarker(m.id);
    const yPending = doc.getMap<PendingPeer>("pending");
    const yIdeas = doc.getArray<Idea>("ideas");
    const tagKey = `tag:${m.id}`;
    let foundText: string | null = null;
    let foundPeer: string | null = null;
    let foundSlots: Record<string, string> | null = null;
    yPending.forEach((p, peerId) => {
      if (foundText) return;
      const text = p?.tagSlots?.[tagKey];
      if (text && text.trim()) {
        foundText = text;
        foundPeer = peerId;
        foundSlots = p.tagSlots;
      }
    });
    if (!foundText || !foundPeer || !foundSlots) return;
    const text: string = foundText;
    const peer: string = foundPeer;
    const slots: Record<string, string> = foundSlots;
    const idea: Idea = { id: crypto.randomUUID(), text: text.trim() };
    // Clear that tag entry from the peer's pending slots.
    const newSlots: Record<string, string> = { ...slots };
    delete newSlots[tagKey];
    doc.transact(() => {
      yIdeas.push([idea]);
      yPending.set(peer, { tagSlots: newSlots });
    });
  }

  const publishMyIdeas = () => {
    if (!meshHandle) return;
    const doc = meshHandle.room.doc;
    const yIdeas = doc.getArray<Idea>("ideas");
    const yPending = doc.getMap<PendingPeer>("pending");
    const texts = [pending1, pending2, pending3].map((t) => t.trim()).filter((t) => !!t);
    if (texts.length === 0) return;
    doc.transact(() => {
      yIdeas.push(texts.map((t) => ({ id: crypto.randomUUID(), text: t })));
      yPending.set(myPeerId, { tagSlots: {} });
    });
    setPending1("");
    setPending2("");
    setPending3("");
  };

  const setPhase = (next: Phase) => {
    if (!meshHandle) return;
    const ySession = meshHandle.room.doc.getMap("session");
    ySession.set("phase", next);
    if (next === "release" && !session.releaseSeed) {
      ySession.set("releaseSeed", Math.floor(Date.now()));
    }
  };

  const toggleVote = (id: string) => {
    if (!meshHandle) return;
    const yVotes = meshHandle.room.doc.getMap<Y.Map<boolean>>("votes");
    let m = yVotes.get(id);
    if (!m) {
      m = new Y.Map<boolean>();
      yVotes.set(id, m);
    }
    if (m.has(myPeerId)) {
      m.delete(myPeerId);
    } else {
      let used = 0;
      yVotes.forEach((mm) => {
        if (mm.has(myPeerId)) used += 1;
      });
      if (used >= 3) return;
      m.set(myPeerId, true);
    }
  };

  const myUsedDots = Object.values(votes).filter((s) => s.has(myPeerId)).length;
  const dotsLeft = 3 - myUsedDots;

  const writeRemainingMs = Math.max(0, session.durationMs - (now - session.startedAt));
  const writeRemainingS = Math.ceil(writeRemainingMs / 1000);

  const arm = () => setArmed(true);

  const shuffledIdeas = useMemo(() => {
    if (session.releaseSeed === 0) return ideas;
    return seededShuffle(ideas, session.releaseSeed);
  }, [ideas, session.releaseSeed]);

  const sortedByVotes = useMemo(() => {
    return [...ideas].sort((a, b) => (votes[b.id]?.size ?? 0) - (votes[a.id]?.size ?? 0));
  }, [ideas, votes]);

  const submittedCount = [pending1, pending2, pending3].filter((idea) => idea.trim()).length;
  const phaseGuidance: Record<Phase, string> = {
    write: "Write privately. Nothing you type is visible to the room yet.",
    release: "Release drafts into a shuffled, anonymous reading board.",
    vote: "Use up to three dots to find the ideas worth carrying forward.",
    results: "Review the strongest signals from the room.",
  };

  if (!armed) {
    return (
      <div className="brain-arm">
        <div className="brain-landing-glow" aria-hidden="true" />
        <main className="brain-landing" aria-labelledby="quiet-draft-title">
          <section className="brain-landing-copy">
            <p className="brain-eyebrow">Private collaborative writing</p>
            <h1 id="quiet-draft-title">Quiet Draft</h1>
            <p className="brain-landing-lede">
              A calmer way to think together. Draft a few possibilities in private, then release
              them into an anonymous room for collective judgment.
            </p>

            <dl className="brain-landing-facts">
              <div>
                <dt>Room</dt>
                <dd>{roomId}</dd>
              </div>
              <div>
                <dt>Format</dt>
                <dd>{writeDurationMin} minute private draft</dd>
              </div>
              <div>
                <dt>Role</dt>
                <dd>{isWall ? "Shared display" : "Writer"}</dd>
              </div>
            </dl>
          </section>

          <aside className="brain-landing-card" aria-label="How Quiet Draft works">
            <div className="brain-landing-card-topline">
              <span className="brain-status-dot" aria-hidden="true" />
              <span>Room ready to join</span>
            </div>
            <ol className="brain-ritual-list">
              <li>
                <span>01</span>
                <div>
                  <strong>Draft in private</strong>
                  <p>Capture up to three thoughts without watching the room.</p>
                </div>
              </li>
              <li>
                <span>02</span>
                <div>
                  <strong>Release together</strong>
                  <p>Ideas arrive as a shuffled, anonymous collection.</p>
                </div>
              </li>
              <li>
                <span>03</span>
                <div>
                  <strong>Find the signal</strong>
                  <p>Use three dots to make the strongest ideas visible.</p>
                </div>
              </li>
            </ol>
            <button
              type="button"
              className="brain-arm-button"
              aria-label="Join the brainstorm — start a private draft"
              onClick={arm}
            >
              {isWall ? "Open the shared display" : "Start a private draft"}
            </button>
            <button type="button" className="brain-arm-secondary" onClick={onOpenSettings}>
              Set the room brief
            </button>
            <p className="brain-landing-footnote">
              {mode === "apriltag"
                ? "Card scanning is enabled for this room."
                : "Your room connects only after you choose to enter."}
            </p>
          </aside>
        </main>
      </div>
    );
  }

  return (
    <main className="brain-stage" aria-labelledby="quiet-draft-session">
      <MeshShellConnectionBridge room={reportedRoom} />
      <header className="brain-session-header">
        <div>
          <p className="brain-eyebrow">Quiet Draft</p>
          <h1 id="quiet-draft-session">
            {session.phase === "write"
              ? "Make space for a better thought."
              : "The room is thinking together."}
          </h1>
        </div>
        <div className="brain-hud" aria-live="polite">
          <span className="brain-presence-dot" aria-hidden="true" />
          <span>
            {peers + 1} {peers === 0 ? "writer" : "writers"}
          </span>
          <span aria-hidden="true">·</span>
          <span>{session.phase}</span>
          {lastMarker !== null ? (
            <>
              <span aria-hidden="true">·</span>
              <span>card #{lastMarker}</span>
            </>
          ) : null}
        </div>
      </header>

      {isWall && mode === "apriltag" && session.phase === "release" && (
        <canvas
          ref={previewRef}
          className="brain-preview"
          width={240}
          height={180}
          aria-label="ArUco scanner preview"
        />
      )}

      <nav className="brain-phase-bar" aria-label="Workshop phases">
        {(["write", "release", "vote", "results"] as Phase[]).map((p) => (
          <button
            key={p}
            type="button"
            className={"brain-phase-btn" + (session.phase === p ? " brain-phase-active" : "")}
            onClick={() => setPhase(p)}
            aria-current={session.phase === p ? "step" : undefined}
          >
            {p}
          </button>
        ))}
      </nav>

      <section className="brain-brief" aria-label="Writing brief">
        <p className="brain-brief-label">Today&apos;s prompt</p>
        <p className="brain-prompt">{session.prompt}</p>
        <p className="brain-phase-guidance">{phaseGuidance[session.phase]}</p>
      </section>

      {session.phase === "write" && (
        <section className="brain-write-layout" aria-label="Private writing workspace">
          <div className="brain-clock-panel">
            <p className="brain-clock-label">Private draft window</p>
            <div
              className="brain-countdown"
              aria-label={`${formatClock(writeRemainingS)} remaining`}
            >
              {formatClock(writeRemainingS)}
            </div>
            <p className="brain-clock-note">
              {isWall
                ? "This display is holding the brief while writers contribute privately."
                : `${submittedCount} of 3 draft${submittedCount === 1 ? "" : "s"} started`}
            </p>
          </div>
          {!isWall && (
            <div className="brain-write">
              <div className="brain-write-heading">
                <p>Private notebook</p>
                <span>Only you can see these until release.</span>
              </div>
              {[1, 2, 3].map((slot) => (
                <label key={slot} className="brain-draft-card">
                  <span>Draft {slot}</span>
                  <textarea
                    className="brain-textarea"
                    aria-label={`Idea ${slot}`}
                    placeholder={
                      slot === 1 ? "Start with the most obvious possibility…" : "Add another angle…"
                    }
                    value={slot === 1 ? pending1 : slot === 2 ? pending2 : pending3}
                    onChange={(e) => {
                      if (slot === 1) setPending1(e.target.value);
                      if (slot === 2) setPending2(e.target.value);
                      if (slot === 3) setPending3(e.target.value);
                    }}
                    rows={2}
                  />
                </label>
              ))}
              <div className="brain-write-actions">
                <button type="button" onClick={() => setPhase("release")}>
                  I&apos;m done — go to release
                </button>
              </div>
            </div>
          )}
          {isWall && (
            <div className="brain-wall-note">The shared display never exposes private drafts.</div>
          )}
        </section>
      )}

      {session.phase === "release" && (
        <section className="brain-board-stage" aria-label="Anonymous idea board">
          {!isWall && mode === "tap" ? (
            <div className="brain-release-tap">
              <p className="brain-board-kicker">Anonymous board</p>
              <h2>Ready when you are.</h2>
              <p>Release only the drafts you want the room to consider.</p>
              <button
                type="button"
                onClick={publishMyIdeas}
                disabled={!pending1.trim() && !pending2.trim() && !pending3.trim()}
              >
                Release my {submittedCount} idea{submittedCount === 1 ? "" : "s"}
              </button>
            </div>
          ) : !isWall && mode === "apriltag" ? (
            <div className="brain-release-tag">
              <p className="brain-board-kicker">Card release</p>
              Hold each tagged card up to the shared display. Your ideas will appear here as they
              are published.
            </div>
          ) : null}
          <div
            className="brain-grid"
            data-testid="brain-pool"
            data-idea-count={shuffledIdeas.length}
          >
            {shuffledIdeas.map((i) => (
              <div key={i.id} className="brain-card" data-testid="brain-idea" data-idea-id={i.id}>
                {i.text}
              </div>
            ))}
            {shuffledIdeas.length === 0 && (
              <div className="brain-empty">The board is waiting for its first released draft.</div>
            )}
          </div>
        </section>
      )}

      {session.phase === "vote" && (
        <section className="brain-board-stage" aria-label="Collaborative vote">
          <div className="brain-vote-hud">
            <span className="brain-board-kicker">Your signal</span>
            <strong>
              {dotsLeft} dot{dotsLeft === 1 ? "" : "s"} left
            </strong>
          </div>
          <div className="brain-grid">
            {shuffledIdeas.map((i) => {
              const count = votes[i.id]?.size ?? 0;
              const mine = votes[i.id]?.has(myPeerId) ?? false;
              return (
                <button
                  key={i.id}
                  type="button"
                  onClick={() => toggleVote(i.id)}
                  data-testid="brain-vote-card"
                  data-idea-id={i.id}
                  data-vote-count={count}
                  className={"brain-card brain-card-clickable" + (mine ? " brain-card-mine" : "")}
                >
                  <span>{i.text}</span>
                  {count > 0 ? <span className="brain-card-dots">●{count}</span> : null}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {session.phase === "results" && (
        <section className="brain-board-stage" aria-label="Workshop results">
          <div className="brain-results-intro">
            <p className="brain-board-kicker">What the room chose</p>
            <h2>The strongest signals, in order.</h2>
          </div>
          <div className="brain-grid">
            {sortedByVotes.map((i, idx) => (
              <div key={i.id} className={"brain-card" + (idx < 3 ? " brain-card-top" : "")}>
                <span>{i.text}</span>
                <span className="brain-card-dots">●{votes[i.id]?.size ?? 0}</span>
              </div>
            ))}
            {sortedByVotes.length === 0 && <div className="brain-empty">No votes yet.</div>}
          </div>
        </section>
      )}
    </main>
  );
}

function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}
